import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw, Loader2, ExternalLink, Calendar } from "lucide-react";

// SPC graphics — public, refreshed by SPC ~hourly during the day.
// We append a cache-busting query so the browser fetches fresh ones.
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PROXY = (path) => `${BACKEND}/api/spc-proxy?path=${encodeURIComponent(path)}`;

// SPC graphics — public, refreshed by SPC ~hourly during the day.
// We proxy through the backend to bypass Chrome's ORB blocking on cross-origin
// cache-control:private images, and append a cache-busting query.
const SPC = {
  day1: {
    label: "Day 1",
    desc: "Today's convective outlook · issued 06z / 13z / 16:30z / 20z / 01z",
    images: [
      { name: "Categorical",         path: "/products/outlook/day1otlk.png" },
      { name: "Tornado Probability", path: "/products/outlook/day1probotlk_torn.png" },
      { name: "Hail Probability",    path: "/products/outlook/day1probotlk_hail.png" },
      { name: "Wind Probability",    path: "/products/outlook/day1probotlk_wind.png" },
    ],
  },
  day2: {
    label: "Day 2",
    desc: "Tomorrow's outlook · issued 06z / 17:30z",
    images: [
      { name: "Categorical", path: "/products/outlook/day2otlk.png" },
    ],
  },
  day3: {
    label: "Day 3",
    desc: "Day after tomorrow · issued 07:30z",
    images: [
      { name: "Categorical", path: "/products/outlook/day3otlk.png" },
    ],
  },
  day48: {
    label: "Day 4-8",
    desc: "Extended severe weather outlook",
    images: [
      { name: "Days 4-8 Severe Probability", path: "/products/exper/day4-8/day48prob.gif" },
    ],
  },
  meso: {
    label: "Mesoanalysis",
    desc: "Live surface analysis · key severe parameters · refreshed hourly",
    images: [
      { name: "Sig Tornado Parameter (CIN)",    path: "/exper/mesoanalysis/s19/stpc/stpc.gif" },
      { name: "Supercell Composite",            path: "/exper/mesoanalysis/s19/scp/scp.gif" },
      { name: "Surface-Based CAPE",             path: "/exper/mesoanalysis/s19/sbcp/sbcp.gif" },
      { name: "Mixed-Layer CAPE",               path: "/exper/mesoanalysis/s19/mlcp/mlcp.gif" },
      { name: "0-1 km Storm-Relative Helicity", path: "/exper/mesoanalysis/s19/srh1/srh1.gif" },
      { name: "0-6 km Bulk Shear",              path: "/exper/mesoanalysis/s19/shr6/shr6.gif" },
      { name: "Effective Bulk Shear",           path: "/exper/mesoanalysis/s19/eshr/eshr.gif" },
    ],
  },
};

const TAB_ORDER = ["day1", "day2", "day3", "day48", "meso"];

// Refresh cadence (ms)
const AUTO_REFRESH_MS = 5 * 60 * 1000; // 5 min

export default function SpcOutlook() {
  const [activeTab, setActiveTab] = useState("day1");
  const [cacheBust, setCacheBust] = useState(Date.now());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loadingCount, setLoadingCount] = useState(0);

  const tab = SPC[activeTab];

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => setCacheBust(Date.now()), AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh]);

  const refresh = () => setCacheBust(Date.now());

  const lastUpdated = useMemo(
    () => new Date(cacheBust).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    [cacheBust]
  );

  const onImgLoadStart = () => setLoadingCount((c) => c + 1);
  const onImgLoadEnd = () => setLoadingCount((c) => Math.max(0, c - 1));

  return (
    <div className="grain" data-testid="spc-page">
      {/* Header */}
      <section className="pt-32 pb-8">
        <div className="container-vg">
          <div className="overline">SPC · Storm Prediction Center</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase tracking-tighter mt-4">
            Daily severe <span className="text-yellow-400">outlooks &amp; models.</span>
          </h1>
          <p className="text-zinc-400 mt-5 max-w-2xl text-lg leading-relaxed">
            Live convective outlooks and mesoscale analysis from the NWS Storm Prediction Center.
            Auto-refreshes every {Math.floor(AUTO_REFRESH_MS / 60000)} minutes.
          </p>
        </div>
      </section>

      <section className="pb-32">
        <div className="container-vg">
          {/* Tabs + controls */}
          <div className="card-vg p-0 overflow-hidden">
            <div className="flex flex-wrap items-center gap-1 border-b border-white/10 bg-[#0d0f12]">
              <div className="flex flex-wrap" role="tablist" data-testid="spc-tablist">
                {TAB_ORDER.map((k) => {
                  const t = SPC[k];
                  const active = activeTab === k;
                  return (
                    <button
                      key={k}
                      onClick={() => setActiveTab(k)}
                      role="tab"
                      aria-selected={active}
                      className={`px-5 py-4 text-xs font-mono uppercase tracking-wider border-b-2 -mb-px transition-colors ${
                        active
                          ? "border-yellow-400 text-yellow-400"
                          : "border-transparent text-zinc-400 hover:text-white"
                      }`}
                      data-testid={`spc-tab-${k}`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>

              <div className="ml-auto flex items-center gap-2 px-4 py-2">
                <span className="hidden md:inline text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                  Updated {lastUpdated}
                </span>
                <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400 cursor-pointer select-none" data-testid="spc-auto-refresh-label">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="w-4 h-4 accent-yellow-400 cursor-pointer"
                    data-testid="spc-auto-refresh"
                  />
                  Auto
                </label>
                <button
                  onClick={refresh}
                  className="text-xs font-mono uppercase tracking-wider border border-white/10 hover:border-yellow-400 hover:text-yellow-400 text-zinc-400 px-3 py-2 inline-flex items-center gap-2"
                  data-testid="spc-refresh"
                >
                  {loadingCount > 0 ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Refresh
                </button>
              </div>
            </div>

            {/* Tab body */}
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
                <div>
                  <div className="overline">{tab.label}</div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold mt-2">{tab.label} Outlook</h2>
                  <p className="text-zinc-400 mt-2 text-sm flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-yellow-400" /> {tab.desc}
                  </p>
                </div>
                <a
                  href={`https://www.spc.noaa.gov/products/${activeTab === "day48" ? "exper/day4-8/" : "outlook/"}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-mono uppercase tracking-wider border border-white/10 hover:border-yellow-400 hover:text-yellow-400 text-zinc-400 px-3 py-2 inline-flex items-center gap-2"
                  data-testid="spc-source-link"
                >
                  View on SPC <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <div className={`grid gap-6 ${tab.images.length === 1 ? "grid-cols-1" : tab.images.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-2"}`}>
                {tab.images.map((img) => (
                  <figure
                    key={img.name}
                    className="border border-white/10 bg-[#0d0f12]"
                    data-testid={`spc-figure-${img.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <div className="overline px-4 py-3 border-b border-white/10 flex items-center justify-between">
                      <span>{img.name}</span>
                      <a
                        href={`https://www.spc.noaa.gov${img.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zinc-500 hover:text-yellow-400 text-[10px] tracking-wider flex items-center gap-1"
                      >
                        OPEN <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="relative bg-white">
                      <img
                        src={`${PROXY(img.path)}&t=${cacheBust}`}
                        alt={img.name}
                        className="w-full h-auto block"
                        loading="lazy"
                        onLoadStart={onImgLoadStart}
                        onLoad={onImgLoadEnd}
                        onError={onImgLoadEnd}
                        data-testid={`spc-img-${img.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      />
                    </div>
                  </figure>
                ))}
              </div>
            </div>
          </div>

          {/* Footer reference cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            <a href="https://www.spc.noaa.gov/products/watch/" target="_blank" rel="noopener noreferrer" className="card-vg p-6 group" data-testid="spc-link-watches">
              <div className="overline group-hover:!text-yellow-400 transition-colors">Active Watches</div>
              <div className="font-heading text-2xl font-bold mt-3 group-hover:text-yellow-400 transition-colors">Tornado &amp; Severe T-Storm Watches</div>
              <div className="text-sm text-zinc-400 mt-2">Live SPC watch products with valid times and parameters.</div>
              <div className="mt-3 text-xs font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-2">spc.noaa.gov/products/watch <ExternalLink className="w-3 h-3" /></div>
            </a>
            <a href="https://www.spc.noaa.gov/products/md/" target="_blank" rel="noopener noreferrer" className="card-vg p-6 group" data-testid="spc-link-mcd">
              <div className="overline group-hover:!text-yellow-400 transition-colors">Mesoscale Discussions</div>
              <div className="font-heading text-2xl font-bold mt-3 group-hover:text-yellow-400 transition-colors">Live MCDs</div>
              <div className="text-sm text-zinc-400 mt-2">Forecaster reasoning on ongoing severe weather threats.</div>
              <div className="mt-3 text-xs font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-2">spc.noaa.gov/products/md <ExternalLink className="w-3 h-3" /></div>
            </a>
            <a href="https://www.spc.noaa.gov/exper/mesoanalysis/" target="_blank" rel="noopener noreferrer" className="card-vg p-6 group" data-testid="spc-link-meso">
              <div className="overline group-hover:!text-yellow-400 transition-colors">Full Mesoanalysis</div>
              <div className="font-heading text-2xl font-bold mt-3 group-hover:text-yellow-400 transition-colors">100+ parameter charts</div>
              <div className="text-sm text-zinc-400 mt-2">All real-time surface analysis fields on the SPC site.</div>
              <div className="mt-3 text-xs font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-2">spc.noaa.gov/exper/mesoanalysis <ExternalLink className="w-3 h-3" /></div>
            </a>
          </div>

          <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider text-center mt-10 max-w-2xl mx-auto leading-relaxed">
            Imagery sourced directly from www.spc.noaa.gov. For official watches, warnings, and reasoning, refer to the NWS / Storm Prediction Center.
          </p>
        </div>
      </section>
    </div>
  );
}
